import { Box } from '@mui/material';
import { LoaderAbsolute } from '@/components/loader/loader';
import { Autocomplete, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS } from 'date-fns/locale';
import { formatDate } from '@/lib/formatters';

interface ReportingFiltersProps {
  filters: any;
  setFilters: any;
  filterData: any;
  loading: boolean;
}

// Design filter-bar chip look — compact, rounded, light-fill fields that sit in
// the bordered "Filters" bar. Styling only; the controls keep their real
// options/handlers so filtering still applies live.
const fieldSx = {
  // Share the row equally and shrink to fit so all filters stay on ONE line
  // (minWidth:0 lets them compress below their content width instead of wrapping).
  flex: '1 1 0',
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    borderRadius: '9px',
    background: '#F9FBFE',
    fontSize: 13,
    fontWeight: 700,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E1E7F0' },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.3px',
    color: '#9BA6B7',
  },
} as const;

export default function ReportingFilters({
  filters,
  setFilters,
  filterData,
  loading,
}: Readonly<ReportingFiltersProps>) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 1,
        background: '#fff',
        border: '1px solid #E7ECF3',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(16,35,58,.04)',
        p: '14px 18px',
        mb: '20px',
      }}>
      {loading && <LoaderAbsolute style={{ width: '100%', height: '100%' }} />}

      <Box
        component="span"
        sx={{
          fontSize: 12.5,
          fontWeight: 700,
          color: '#7A8497',
          mr: 0.5,
          flex: 'none',
        }}>
        Filters
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
        <DatePicker
          label="Date to"
          value={filters.dateToDisplay}
          views={['month', 'year']}
          onChange={(value) => {
            setFilters({
              ...filters,
              dateToDisplay: value,
              dateTo: value ? formatDate(value) : null,
            });
          }}
          slotProps={{
            textField: {
              size: 'small',
              sx: fieldSx,
            },
          }}
        />
      </LocalizationProvider>

      <Autocomplete
        disableClearable
        disablePortal
        freeSolo
        size="small"
        sx={fieldSx}
        options={filterData.currency}
        value={filters.currency}
        onChange={(e, value) => {
          setFilters({ ...filters, currency: value });
        }}
        renderInput={(params) => (
          <TextField {...params} label="Currency" size="small" />
        )}
      />

      <Autocomplete
        disablePortal
        freeSolo
        size="small"
        sx={fieldSx}
        options={filterData.cloudAccount}
        value={filters.cloudAccount?.name}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => {
          setFilters({ ...filters, cloudAccount: value?.id ?? null });
        }}
        renderInput={(params) => (
          <TextField {...params} label="Cloud Account" size="small" />
        )}
      />

      <Autocomplete
        disablePortal
        freeSolo
        size="small"
        sx={fieldSx}
        options={filterData.department}
        value={filters.department?.name}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => {
          setFilters({ ...filters, department: value?.id ?? null });
        }}
        renderInput={(params) => (
          <TextField {...params} label="Department" size="small" />
        )}
      />

      <Autocomplete
        disablePortal
        freeSolo
        size="small"
        sx={fieldSx}
        options={filterData.system}
        value={filters.system?.name}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => {
          setFilters({ ...filters, system: value?.id ?? null });
        }}
        renderInput={(params) => (
          <TextField {...params} label="System" size="small" />
        )}
      />

      <Autocomplete
        disablePortal
        freeSolo
        size="small"
        sx={fieldSx}
        options={filterData.subscription}
        value={filters.subscription?.display_name}
        getOptionLabel={(option) => option.display_name}
        onChange={(e, value) => {
          setFilters({ ...filters, subscription: value?.subscription_id ?? null });
        }}
        renderInput={(params) => (
          <TextField {...params} label="Subscription" size="small" />
        )}
      />

      <Autocomplete
        disablePortal
        freeSolo
        size="small"
        sx={fieldSx}
        options={filterData.cloudAccountOwner}
        value={filters.cloudAccountOwner?.username}
        getOptionLabel={(option) => option.username}
        onChange={(e, value) => {
          setFilters({ ...filters, cloudAccountOwner: value?.id ?? null });
        }}
        renderInput={(params) => (
          <TextField {...params} label="Cloud Account Owner" size="small" />
        )}
      />

      <Autocomplete
        disablePortal
        freeSolo
        size="small"
        sx={fieldSx}
        options={filterData.owner}
        value={filters.assigned?.username}
        getOptionLabel={(option) => option.username}
        onChange={(e, value) => {
          setFilters({ ...filters, assigned: value?.id ?? null });
        }}
        renderInput={(params) => (
          <TextField {...params} label="Resource Owner" size="small" />
        )}
      />
    </Box>
  );
}
